import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import 'swiper/swiper-bundle.css'

import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, BoxDropFile, Button, Select } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Info } from 'lucide-react'

import { GraphicArea } from '@/common/components/GraphicArea'
import { FileType } from '@/common/models/files'
import { translateFileTypeEnum } from '@/common/utils/translateFileTypeEnum'
import { useUploadArchive } from './hooks/useUploadArchive'
import {
  UploadArchiveSchema,
  UploadArchiveSchemaForm,
} from './schemas/UploadArchiveSchema'

export default function Upload() {
  const { t } = useTranslation('panel')
  const hookForm = useForm<UploadArchiveSchemaForm>({
    resolver: zodResolver(UploadArchiveSchema()),
  })
  const { uploadArchive, isUploadingArchive } = useUploadArchive()

  const handleUploadArchive = async (data: UploadArchiveSchemaForm) => {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('file_type', data.file_type)
    await uploadArchive(formData)
    hookForm.reset()
  }

  const FILE_TYPE_OPTIONS = Object.values(FileType)
    .filter((type) =>
      [FileType.ABACO, FileType.CLAIMS, FileType.LUBRIFICANTS].includes(type),
    )
    .map((type) => ({
      value: type,
      label: translateFileTypeEnum(t, type),
    }))

  return (
    <>
      <HeadProvider>
        <Title>{t('upload_files.browser.title')}</Title>
      </HeadProvider>

      <GraphicArea.Root>
        <GraphicArea.Header className="space-y-2">
          <h2 className="text-2xl font-semibold text-brand-800">
            {t('upload_files.page.title')}
          </h2>

          <h3 className="font-normal text-base text-neutral-800">
            {t('upload_files.page.subtitle')}
          </h3>
        </GraphicArea.Header>

        <GraphicArea.Body>
          <FormProvider {...hookForm}>
            <form className="flex flex-col flex-1 gap-6 h-full">
              <fieldset>
                <BoxDropFile.Root>
                  <BoxDropFile.Input
                    name="file"
                    title={t('upload_files.input_upload.title')}
                    description={t('upload_files.input_upload.description')}
                    accept={{
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                        ['.xlsx'],
                      'text/csv': ['.csv'],
                    }}
                  />
                </BoxDropFile.Root>
              </fieldset>

              <fieldset className="md:max-w-1/2">
                <Select.Single
                  label={t('upload_files.input_select.label')}
                  name="file_type"
                  options={FILE_TYPE_OPTIONS}
                  placeholder={t('upload_files.input_select.placeholder')}
                />
              </fieldset>

              <Alert.Root>
                <Alert.Icon icon={<Info />} />

                <Alert.Body>
                  <Alert.Description>
                    {t('upload_files.page.alert_description')}
                  </Alert.Description>
                </Alert.Body>
              </Alert.Root>
            </form>
          </FormProvider>
        </GraphicArea.Body>

        <GraphicArea.Footer className="flex justify-end items-center">
          <Button.Root
            type="submit"
            onClick={() => hookForm.handleSubmit(handleUploadArchive)()}
            isLoading={isUploadingArchive}
          >
            {t('upload_files.button_submit.label')}
          </Button.Root>
        </GraphicArea.Footer>
      </GraphicArea.Root>
    </>
  )
}
